// =============================================================================
// useMediaQuery Hook
// =============================================================================
// Detects if a CSS media query matches. Used for responsive behavior
// in JavaScript (when CSS alone isn't enough).
//
// EXAMPLE USE CASES:
// - Show hamburger menu on mobile, full nav on desktop
// - Load a video hero on desktop, static image on mobile
// - Change component behavior based on screen size
//
// HOW IT WORKS:
// Uses the browser's built-in matchMedia API, which is the same engine
// that powers CSS @media queries. So the breakpoints stay in sync.
// =============================================================================

import { useState, useEffect } from 'react';

/**
 * Returns true if the given media query matches the current viewport.
 *
 * @param query - A CSS media query string (e.g., "(min-width: 768px)")
 * @returns boolean - true if the media query matches
 *
 * USAGE:
 *   const isTabletOrAbove = useMediaQuery('(min-width: 768px)');
 *   const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
 *   const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    // Check immediately on mount (SSR-safe: defaults to false if window is not available)
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);

    // Update state when the media query match changes
    // (e.g., user resizes their browser window)
    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Listen for changes
    mediaQuery.addEventListener('change', handler);

    // Sync initial state
    setMatches(mediaQuery.matches);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handler);
    };
  }, [query]);

  return matches;
}

// --- Convenience constants for common breakpoints ---
// These match the breakpoints defined in base.css §6

/** Phones and up (≥480px) */
export const BREAKPOINT_SM = '(min-width: 480px)';

/** Tablets and up (≥768px) */
export const BREAKPOINT_MD = '(min-width: 768px)';

/** Small laptops and up (≥1024px) */
export const BREAKPOINT_LG = '(min-width: 1024px)';

/** Desktops and up (≥1280px) */
export const BREAKPOINT_XL = '(min-width: 1280px)';
